import { startAuthentication } from "@simplewebauthn/browser";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { AuthenticatorSetup } from "../components/AuthenticatorSetup";
import { ApiError, get, post } from "../api";
import { Alert, Button, Field } from "../components/ui";
import { useSession } from "../session";

type Step =
  | { step: "email" }
  | { step: "choose"; options: ("passkey" | "password")[] }
  | { step: "password" }
  | { step: "passkey"; requestOptions: unknown }
  | { step: "new_password" }
  | { step: "mfa_setup"; secret: string; otpauthUri: string }
  | { step: "mfa_code"; method: "totp" | "email" };

type AuthResponse = { status: "signed_in"; name: string } | ({ status: "challenge" } & Exclude<Step, { step: "email" | "password" }>);

const LAST_EMAIL_KEY = "vaulted.lastEmail";

function readLastEmail() {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

export function SignIn() {
  const { refresh, signedOutReason } = useSession();
  const mode = useQuery({ queryKey: ["auth-mode"], queryFn: () => get<{ mode: "cognito" | "dev" }>("/api/auth/mode") });
  const [email, setEmail] = useState(readLastEmail);
  const [step, setStep] = useState<Step>({ step: "email" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<AuthResponse>) {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.status === "signed_in") {
        try {
          localStorage.setItem(LAST_EMAIL_KEY, email);
        } catch {
          /* storage unavailable */
        }
        await refresh();
        return;
      }
      const { status: _s, ...next } = res;
      setStep(next as Step);
      if (next.step === "passkey") await usePasskey(next.requestOptions);
    } catch (err) {
      if (err instanceof ApiError && err.code === "expired") setStep({ step: "email" });
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  async function usePasskey(requestOptions: unknown) {
    let credential;
    try {
      credential = await startAuthentication({ optionsJSON: requestOptions as never });
    } catch {
      // Cancelled or no passkey on this device: offer the password instead.
      setStep({ step: "password" });
      setError("Passkey sign-in was cancelled. You can use your password instead.");
      return;
    }
    await run(() => post<AuthResponse>("/api/auth/passkey", { credential }));
  }

  const startWithEmail = (e: FormEvent) => {
    e.preventDefault();
    if (mode.data?.mode === "dev") return run(() => post<AuthResponse>("/api/auth/dev-login", { email }));
    return run(() => post<AuthResponse>("/api/auth/passkey/start", { email }));
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <div className="relative hidden overflow-hidden bg-ink lg:block">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <img src="/favicon.svg" alt="" className="size-12" />
          <div>
            <p className="font-display text-[3.25rem] leading-[1.05]">
              Vaulted
              <br />
              Vitality
            </p>
            <p className="mt-5 max-w-sm text-white/70">Staff sign-in for scheduling, clients, and clinic operations.</p>
          </div>
          <p className="text-sm text-white/50">For authorized staff only. Activity is recorded.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm">
          <img src="/favicon.svg" alt="" className="mb-8 size-11 lg:hidden" />
          <h1 className="page-title">{titleFor(step)}</h1>
          <p className="mt-2 text-ink-soft">{subtitleFor(step, email)}</p>

          <div className="mt-8 flex flex-col gap-4">
            {signedOutReason && step.step === "email" && !error && <Alert tone="info">{signedOutReason}</Alert>}
            {error && <Alert>{error}</Alert>}

            {step.step === "email" && (
              <form onSubmit={startWithEmail} className="flex flex-col gap-4">
                <Field
                  label="Work email"
                  type="email"
                  autoComplete="username webauthn"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button variant="primary" type="submit" busy={busy} className="py-2.5">
                  {mode.data?.mode === "dev" ? "Sign in (development)" : "Continue"}
                </Button>
                {mode.data?.mode === "cognito" && (
                  <Button type="button" variant="quiet" onClick={() => email && setStep({ step: "password" })} disabled={!email}>
                    Use password instead
                  </Button>
                )}
              </form>
            )}

            {step.step === "choose" && (
              <div className="flex flex-col gap-3">
                {step.options.includes("passkey") && (
                  <Button variant="primary" busy={busy} onClick={() => run(() => post<AuthResponse>("/api/auth/passkey/start", { email }))}>
                    Sign in with passkey
                  </Button>
                )}
                <Button variant={step.options.includes("passkey") ? "secondary" : "primary"} onClick={() => setStep({ step: "password" })}>
                  Sign in with password
                </Button>
              </div>
            )}

            {step.step === "passkey" && (
              <div className="flex flex-col gap-3">
                <Button variant="primary" busy={busy} onClick={() => usePasskey(step.requestOptions)}>
                  Try passkey again
                </Button>
                <Button variant="quiet" onClick={() => setStep({ step: "password" })}>
                  Use password instead
                </Button>
              </div>
            )}

            {step.step === "password" && <PasswordForm email={email} busy={busy} onSubmit={(password) => run(() => post<AuthResponse>("/api/auth/password", { email, password }))} />}

            {step.step === "new_password" && <NewPasswordForm busy={busy} onSubmit={(password) => run(() => post<AuthResponse>("/api/auth/new-password", { password }))} />}

            {step.step === "mfa_setup" && (
              <MfaSetup secret={step.secret} uri={step.otpauthUri} busy={busy} onSubmit={(code) => run(() => post<AuthResponse>("/api/auth/mfa-setup", { code }))} />
            )}

            {step.step === "mfa_code" && <CodeForm busy={busy} label={step.method === "email" ? "Code from your email" : "Code from your authenticator app"} onSubmit={(code) => run(() => post<AuthResponse>("/api/auth/mfa", { code }))} />}

            {step.step !== "email" && (
              <button
                className="self-start text-sm font-medium text-muted underline-offset-4 hover:text-ink hover:underline"
                onClick={() => {
                  setStep({ step: "email" });
                  setError(null);
                }}
              >
                Start over with a different email
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function titleFor(s: Step) {
  switch (s.step) {
    case "new_password":
      return "Choose your password";
    case "mfa_setup":
      return "Set up sign-in codes";
    case "mfa_code":
      return "Enter your code";
    default:
      return "Sign in";
  }
}

function subtitleFor(s: Step, email: string) {
  switch (s.step) {
    case "email":
      return "Use your passkey, Face ID, or Touch ID. You can use a password if your device doesn't have one.";
    case "new_password":
      return "Replace the temporary password from your invite email. You'll add a passkey after this.";
    case "mfa_setup":
      return "Your password needs a second step. Scan this code with an authenticator app, such as Google Authenticator or 1Password.";
    case "mfa_code":
      return "Enter the 6-digit code to finish signing in.";
    default:
      return email;
  }
}

function PasswordForm({ email, busy, onSubmit }: { email: string; busy: boolean; onSubmit: (p: string) => void }) {
  const [password, setPassword] = useState("");
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(password);
      }}
    >
      <input type="email" autoComplete="username" value={email} readOnly hidden />
      <Field label="Password" type="password" autoComplete="current-password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      <Button variant="primary" type="submit" busy={busy}>
        Sign in
      </Button>
    </form>
  );
}

function NewPasswordForm({ busy, onSubmit }: { busy: boolean; onSubmit: (p: string) => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!mismatch) onSubmit(password);
      }}
    >
      <Field
        label="New password"
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        autoFocus
        hint="At least 12 characters, with upper and lower case letters, a number, and a symbol."
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Field
        label="Confirm password"
        type="password"
        autoComplete="new-password"
        required
        error={mismatch ? "Passwords don't match." : undefined}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <Button variant="primary" type="submit" busy={busy} disabled={mismatch}>
        Save password
      </Button>
    </form>
  );
}

function MfaSetup({ secret, uri, busy, onSubmit }: { secret: string; uri: string; busy: boolean; onSubmit: (c: string) => void }) {
  return (
    <div className="flex flex-col gap-4">
      <AuthenticatorSetup secret={secret} uri={uri} />
      <CodeForm busy={busy} label="6-digit code" onSubmit={onSubmit} />
    </div>
  );
}

function CodeForm({ label, busy, onSubmit }: { label: string; busy: boolean; onSubmit: (c: string) => void }) {
  const [code, setCode] = useState("");
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(code);
      }}
    >
      <Field
        label={label}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6,8}"
        maxLength={8}
        required
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        className="[&_input]:text-lg [&_input]:tracking-[0.3em]"
      />
      <Button variant="primary" type="submit" busy={busy}>
        Continue
      </Button>
    </form>
  );
}
